"use client"

import type { ReactNode } from "react"
import { ChevronRightIcon } from "@/components/icons/pixel-icons"
import { useMenuLayout } from "./menu-layout-context"

/**
 * Row/panel primitives for the main menu.
 *
 * Layout follows the grouped-settings pattern (section header, rounded panel,
 * one tappable row per setting) but is re-skinned into the game's palette so it
 * sits next to the codex and HUD rather than looking like a web settings page:
 * translucent black panels, amber hairlines, amber-300 headings.
 *
 * Every row is at least 44px tall so it stays a comfortable touch target.
 */

const ROW_BASE = "w-full text-left flex items-center gap-3 px-4 py-3 min-h-[52px]"
const ROW_INTERACTIVE =
  "group transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:bg-white/10"

export const MenuGroup = ({
  title,
  icon,
  children,
}: {
  title: string
  icon?: ReactNode
  children: ReactNode
}) => (
  <section className="mb-5 last:mb-0">
    <header className="flex items-center gap-2 px-1 mb-2">
      {icon && <span className="text-amber-500/70 flex items-center">{icon}</span>}
      <h2 className="text-xs font-bold uppercase tracking-wider text-amber-200/80">{title}</h2>
    </header>
    <div className="rounded-xl bg-black/30 border border-amber-600/20 overflow-hidden divide-y divide-amber-600/10">
      {children}
    </div>
  </section>
)

/** Row that drills into a sub-view. */
export const MenuNavRow = ({
  icon,
  label,
  sublabel,
  value,
  isActive = false,
  onClick,
}: {
  icon?: ReactNode
  label: string
  sublabel?: string
  /** Optional right-aligned summary shown before the chevron */
  value?: ReactNode
  /** Desktop only: highlights the row whose detail pane is showing */
  isActive?: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={isActive ? "true" : undefined}
    className={`${ROW_BASE} ${ROW_INTERACTIVE} ${isActive ? "bg-amber-900/25" : ""}`}
  >
    {icon && (
      <span className={`shrink-0 flex items-center ${isActive ? "text-amber-300" : "text-amber-500/70"}`}>
        {icon}
      </span>
    )}
    <span className="min-w-0 flex-1">
      <span className={`block text-sm font-bold leading-tight ${isActive ? "text-amber-200" : "text-gray-100"}`}>
        {label}
      </span>
      {sublabel && (
        <span className="block text-xs leading-snug text-gray-400 mt-0.5 line-clamp-2">
          {sublabel}
        </span>
      )}
    </span>
    {value && <span className="shrink-0 text-xs font-bold text-amber-300 tabular-nums">{value}</span>}
    <span className="text-gray-500 shrink-0 transition-transform group-hover:translate-x-0.5">
      <ChevronRightIcon size={16} />
    </span>
  </button>
)

/** Read-only row with a right-aligned value or control. */
export const MenuValueRow = ({
  icon,
  label,
  sublabel,
  children,
}: {
  icon?: ReactNode
  label: string
  sublabel?: string
  children?: ReactNode
}) => (
  <div className={ROW_BASE}>
    {icon && <span className="shrink-0 flex items-center text-amber-500/70">{icon}</span>}
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-bold leading-tight text-gray-100">{label}</span>
      {sublabel && (
        <span className="block text-xs leading-snug text-gray-400 mt-0.5">{sublabel}</span>
      )}
    </span>
    {children && <span className="shrink-0 flex items-center gap-2">{children}</span>}
  </div>
)

/** Destructive or primary action rendered as a full row. */
export const MenuActionRow = ({
  icon,
  label,
  sublabel,
  onClick,
  disabled = false,
  danger = false,
}: {
  icon?: ReactNode
  label: string
  sublabel?: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`${ROW_BASE} ${ROW_INTERACTIVE} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
  >
    {icon && (
      <span className={`shrink-0 flex items-center ${danger ? "text-red-400" : "text-amber-500/70"}`}>
        {icon}
      </span>
    )}
    <span className="min-w-0 flex-1">
      <span
        className={`block text-sm font-bold leading-tight ${danger ? "text-red-400" : "text-gray-100"}`}
      >
        {label}
      </span>
      {sublabel && (
        <span className="block text-xs leading-snug text-gray-400 mt-0.5">{sublabel}</span>
      )}
    </span>
  </button>
)

export const MenuToggleRow = ({
  icon,
  label,
  sublabel,
  checked,
  onChange,
}: {
  icon?: ReactNode
  label: string
  sublabel?: string
  checked: boolean
  onChange: (next: boolean) => void
}) => (
  <MenuValueRow icon={icon} label={label} sublabel={sublabel}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? "bg-amber-600" : "bg-slate-700"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-[left] ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  </MenuValueRow>
)

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

export const MenuSegmentedRow = <T extends string>({
  icon,
  label,
  sublabel,
  options,
  value,
  onChange,
}: {
  icon?: ReactNode
  label: string
  sublabel?: string
  options: ReadonlyArray<SegmentedOption<T>>
  value: T
  onChange: (next: T) => void
}) => (
  <div className={`${ROW_BASE} flex-wrap`}>
    {icon && <span className="shrink-0 flex items-center text-amber-500/70">{icon}</span>}
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-bold leading-tight text-gray-100">{label}</span>
      {sublabel && (
        <span className="block text-xs leading-snug text-gray-400 mt-0.5">{sublabel}</span>
      )}
    </span>
    <div
      role="radiogroup"
      aria-label={label}
      className="flex rounded-full bg-black/40 border border-amber-600/20 p-0.5"
    >
      {options.map((option) => {
        const isSelected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
              isSelected ? "bg-amber-600 text-black" : "text-gray-400 hover:text-gray-100"
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  </div>
)

/** Compact label/value pair for stat blocks inside a detail view. */
export const MenuStatRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-bold text-gray-100 tabular-nums">{value}</span>
  </div>
)

/**
 * Title + optional blurb at the top of a detail view. On the mobile drill-down
 * the sheet header already shows this title, so only the blurb renders there.
 */
export const MenuDetailHeading = ({
  title,
  blurb,
}: {
  title: string
  blurb?: string
}) => {
  const { isNarrow } = useMenuLayout()
  return (
    <div className="mb-4">
      {!isNarrow && <h3 className="text-xl font-bold text-amber-300">{title}</h3>}
      {blurb && <p className={`text-sm text-gray-400 ${isNarrow ? "" : "mt-1"}`}>{blurb}</p>}
    </div>
  )
}

/** Inline success/error feedback under an action. */
export const MenuNotice = ({
  tone,
  children,
}: {
  tone: "error" | "success" | "info"
  children: ReactNode
}) => (
  <div
    className={`mt-3 text-xs rounded-lg px-3 py-2 border ${
      tone === "error"
        ? "text-red-300 bg-red-950/40 border-red-900/60"
        : tone === "success"
          ? "text-green-300 bg-green-950/40 border-green-900/60"
          : "text-amber-200 bg-amber-950/40 border-amber-900/60"
    }`}
  >
    {children}
  </div>
)

export const MenuEmptyState = ({ children }: { children: ReactNode }) => (
  <div className="px-4 py-6 text-center text-sm text-gray-500">{children}</div>
)
